<script setup lang="ts">
import { computed, defineProps, RenderFunction } from 'vue'
import { KycDocumentVerificationStatus, KycDocumentVerificationType } from '../../../../typings'
import { PersonalAccountData } from '../../../../utilities/account/useAccountDataPersonal'
import AccountKycDocument from './AccountKycDocument.vue'
import { KycDocument } from '../../../../utilities/api'
import { LegalAccountData } from '../../../../utilities/account/useAccountDataLegal'
import AccountKycDocumentsUpload from './AccountKycDocumentsUpload.vue'

export interface KycDocumentSettings {
    header: string
    accepted?: boolean
    icon?: RenderFunction
    documents: KycDocument[]
}

const props = defineProps<{
    kycDocuments: KycDocumentSettings
    verificationType: KycDocumentVerificationType
    store: PersonalAccountData | LegalAccountData
}>()

const hasPendingKycDocuments = computed(
    () =>
        !!props.kycDocuments.documents.find(
            (kycDoc) => kycDoc.verification_status === KycDocumentVerificationStatus.IN_PROGRESS
        )
)

const requiredLegalTypes = [
    'association-register-extract',
    'list-of-shareholders',
    'articles-of-association',
    'id-of-beneficial-owners',
    'id-of-managing-director',
    'proof-of-authority-to-represent',
] as const

const missingRequiredLegalTypes = computed(() => {
    if (props.verificationType !== KycDocumentVerificationType.KycLegal) {
        return []
    }

    const presentTypes = new Set(
        props.kycDocuments.documents
            .filter((doc) =>
                [KycDocumentVerificationStatus.IN_PROGRESS, KycDocumentVerificationStatus.ACCEPTED].includes(
                    doc.verification_status as KycDocumentVerificationStatus
                )
            )
            .map((doc) => doc.type)
    )

    return requiredLegalTypes.filter((type) => !presentTypes.has(type as any))
})

const showAddButton = computed(
    () =>
        props.verificationType === 'kyc_legal' ||
        (props.kycDocuments.documents.length > 0 && !hasPendingKycDocuments.value)
)
const newKycUploadOnInit = computed(() => props.kycDocuments.documents.length === 0)

function updateKycDocs() {
    props.store.fetchKycDocuments()
    props.store.fetchBankAccounts()
}
</script>

<template>
    <div>
        <template v-if="kycDocuments.documents.length > 0">
            <div class="mt-5 !space-y-4">
                <AccountKycDocument
                    v-for="(kycDoc, idx) in kycDocuments.documents"
                    :key="idx"
                    class="my-2"
                    :kyc-document="kycDoc"
                    :verification-type="verificationType"
                    :icon="kycDocuments.icon"
                    @updated-bank-account="updateKycDocs" />
            </div>
        </template>

        <AccountKycDocumentsUpload
            class="mt-4"
            :icon="kycDocuments.icon"
            :store="store"
            :new-kyc-upload-on-init="newKycUploadOnInit"
            :has-pending-kyc-documents="hasPendingKycDocuments"
            :missing-required-legal-types="missingRequiredLegalTypes"
            :show-add-button="showAddButton"
            :verification-type="verificationType" />
    </div>
</template>
